import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { useSEO, generateServiceSchema, generateFAQSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function HeadlightTaillightTintingPage() {
  const tintOptions = [
    {
      name: 'Smoke Tint',
      price: 'Popular Choice',
      image: 'https://public.readdy.ai/ai/img_res/399dd878f77814abe8484a7b08b74075.jpg',
      features: [
        'Subtle dark appearance',
        'Maintains light output',
        'UV and scratch resistant',
        'Easy to remove if needed',
        'Professional installation',
        'Compliant with regulations',
      ],
      benefits: 'Perfect balance of style and functionality. Gives your vehicle a sleek, modern look while maintaining safety.',
    },
    {
      name: 'Dark Smoke Tint',
      price: 'Bold Statement',
      image: 'https://readdy.ai/api/search-image?query=dark-smoke-tinted-car-taillights-on-sports-car-deep-charcoal-translucent-film-aggressive-styling-modern-LED-tail-lamps-professional-automotive-customization-close-up-photography-premium-quality-finish&width=600&height=400&seq=dark-smoke-tail-1&orientation=landscape',
      features: [
        'Dramatic dark finish',
        'Enhanced vehicle aesthetics',
        'Premium quality film',
        'Weather resistant',
        'Long-lasting durability',
        'Expert application',
      ],
      benefits: 'Maximum style impact for those who want their vehicle to stand out. Bold, aggressive appearance.',
    },
  ];

  const faqs = [
    {
      question: 'Will tinting affect my headlight brightness?',
      answer: 'When professionally installed with quality film, the impact on brightness is minimal. Our films are designed to maintain safe light output while providing the desired aesthetic. We ensure all installations meet safety standards.',
    },
    {
      question: 'How long does headlight and taillight tinting take?',
      answer: 'Most vehicles can be completed in 1-2 hours. The process includes careful preparation, precise application, and quality inspection to ensure perfect results.',
    },
    {
      question: 'Can the tint be removed if needed?',
      answer: 'Yes! Our professional-grade films can be safely removed without damaging your lights. This makes it easy to change styles or return to stock appearance if desired.',
    },
    {
      question: 'What warranty do you offer on light tinting?',
      answer: 'We provide a warranty covering peeling, bubbling, and fading. The specific warranty period depends on the film type chosen. All installations are backed by our satisfaction guarantee.',
    },
    {
      question: 'Do you tint both headlights and taillights?',
      answer: 'Yes! We offer tinting services for headlights, taillights, fog lights, and side markers. We can tint individual lights or complete packages based on your preferences.',
    },
  ];

  useSEO({
    title: 'Headlight & Taillight Tinting Baltimore - Smoke Tint | U.B.K Window Tint',
    description: 'Professional headlight and taillight tinting in Baltimore, MD. Smoke and dark smoke options. Add style and protection while maintaining safety. Expert installation. Call (443) 800-0348.',
    keywords: 'headlight tinting Baltimore, taillight tint Maryland, smoke tint, light tinting, automotive customization',
    canonical: '/headlight-taillight-tinting',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateServiceSchema(
          'Headlight & Taillight Tinting',
          'Professional headlight and taillight tinting services. Smoke and dark smoke options for enhanced vehicle appearance while maintaining safety.',
          '/headlight-taillight-tinting'
        ),
        generateFAQSchema(faqs),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Headlight & Taillight Tinting', url: '/headlight-taillight-tinting' },
        ]),
      ],
    },
  });

  return (
    <div className="min-h-screen">
      <Navigation />
      <main className="pt-24 max-md:pt-20">
        {/* Hero Section */}
        <section className="relative pt-12 md:pt-16 pb-8 md:pb-12 bg-gradient-to-br from-secondary via-gray-800 to-secondary overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'linear-gradient(45deg, transparent 48%, white 49%, white 51%, transparent 52%)',
              backgroundSize: '20px 20px',
            }}></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 items-center">
              <div className="text-white">
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-3 md:mb-6 leading-tight">
                  Headlight &amp; Taillight Tinting Services
                </h1>
                <p className="text-sm md:text-xl text-gray-300 mb-4 md:mb-8 leading-relaxed">
                  Transform your vehicle's appearance with professional light tinting. Add style and protection while maintaining safety and legal compliance.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 md:gap-4">
                  <Link to="/contact">
                    <Button size="lg" className="text-xs md:text-base py-2 md:py-3 px-4 md:px-6">
                      <i className="ri-calendar-line mr-1 md:mr-2"></i>
                      Request Appointment
                    </Button>
                  </Link>
                  <a href="tel:4438000348">
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-secondary text-xs md:text-base py-2 md:py-3 px-4 md:px-6">
                      <i className="ri-phone-line mr-1 md:mr-2"></i>
                      Get Free Quote
                    </Button>
                  </a>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=professional-car-headlight-tinting-service-modern-luxury-vehicle-with-tinted-headlights-and-taillights-automotive-customization-studio-setting-sleek-dark-smoke-finish-premium-quality-installation-clean-professional-environment&width=800&height=600&seq=hero-light-tint-1&orientation=landscape"
                  alt="Professional Headlight and Taillight Tinting"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Tint Options Section */}
        <section className="py-8 md:py-24 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-16">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                Choose Your Tint Style
              </h2>
              <p className="text-sm md:text-xl text-gray-600 max-w-3xl mx-auto">
                Premium tint films designed specifically for automotive lights. Professional installation ensures perfect results.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 max-w-5xl mx-auto">
              {tintOptions.map((tint, index) => (
                <div key={tint.name} className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 h-full flex flex-col">
                  <div className="relative h-40 md:h-64">
                    <img
                      src={tint.image}
                      alt={tint.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 md:top-4 right-2 md:right-4 bg-primary text-white px-2 md:px-4 py-1 md:py-2 rounded-full font-bold text-xs md:text-base">
                      {tint.price}
                    </div>
                  </div>
                  
                  <div className="p-4 md:p-6 flex-1 flex flex-col">
                    <h3 className="text-lg md:text-2xl font-bold text-secondary mb-2 md:mb-4">{tint.name}</h3>
                    <p className="text-xs md:text-base text-gray-600 mb-3 md:mb-6 leading-relaxed">{tint.benefits}</p>
                    
                    <div className="space-y-1.5 md:space-y-3 mb-4 md:mb-6 flex-1">
                      {tint.features.map((feature) => (
                        <div key={feature} className="flex items-start space-x-2 md:space-x-3">
                          <i className="ri-check-line text-primary text-base md:text-xl mt-0.5 w-4 h-4 md:w-5 md:h-5 flex items-center justify-center flex-shrink-0"></i>
                          <span className="text-xs md:text-base text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <Link to="/contact">
                      <Button fullWidth className="text-xs md:text-base py-2 md:py-3">Get Quote</Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-8 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-16">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                Our Installation Process
              </h2>
              <p className="text-sm md:text-xl text-gray-600 max-w-3xl mx-auto">
                Professional installation ensures perfect results that last. Here's what to expect.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
              {[
                {
                  step: '01',
                  title: 'Consultation',
                  description: 'We discuss your style preferences and show tint samples. We help you choose the perfect shade that meets legal requirements.',
                  icon: 'ri-chat-check-line',
                },
                {
                  step: '02',
                  title: 'Preparation',
                  description: 'Lights are thoroughly cleaned and prepped. We ensure a dust-free surface for perfect film adhesion.',
                  icon: 'ri-brush-line',
                },
                {
                  step: '03',
                  title: 'Installation',
                  description: 'Expert technicians apply film with precision. We use professional techniques for bubble-free, seamless results.',
                  icon: 'ri-tools-line',
                },
                {
                  step: '04',
                  title: 'Quality Check',
                  description: 'Final inspection ensures perfect installation. We test light output and provide care instructions.',
                  icon: 'ri-shield-check-line',
                },
              ].map((process, index) => (
                <div key={process.step} className="text-center">
                  <div className="w-12 h-12 md:w-20 md:h-20 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-2 md:mb-4">
                    <i className={`${process.icon} text-white text-xl md:text-3xl`}></i>
                  </div>
                  <div className="text-2xl md:text-4xl font-bold text-gray-200 mb-1 md:mb-2">{process.step}</div>
                  <h3 className="text-base md:text-xl font-bold text-secondary mb-1.5 md:mb-3">{process.title}</h3>
                  <p className="text-xs md:text-base text-gray-600 leading-relaxed">{process.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-8 md:py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-16">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-sm md:text-xl text-gray-600">
                Everything you need to know about light tinting
              </p>
            </div>

            <div className="space-y-2 md:space-y-4">
              {faqs.map((faq, index) => (
                <details key={index} className="bg-white rounded-xl shadow-md overflow-hidden group">
                  <summary className="px-4 md:px-6 py-3 md:py-5 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <h3 className="text-sm md:text-lg font-semibold text-secondary pr-4">{faq.question}</h3>
                    <i className="ri-arrow-down-s-line text-xl md:text-2xl text-primary group-open:rotate-180 transition-transform w-5 h-5 md:w-6 md:h-6 flex items-center justify-center flex-shrink-0"></i>
                  </summary>
                  <div className="px-4 md:px-6 pb-3 md:pb-5 text-xs md:text-base text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-8 md:py-24 bg-gradient-to-br from-primary to-accent text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-6">
              Ready to Upgrade Your Lights?
            </h2>
            <p className="text-sm md:text-xl mb-4 md:mb-8 opacity-90">
              Get professional headlight and taillight tinting. Book your appointment today!
            </p>
            <div className="flex flex-col sm:flex-row gap-2 md:gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" variant="secondary" className="min-w-[160px] md:min-w-[200px] text-xs md:text-base py-2 md:py-3">
                  <i className="ri-calendar-line mr-1 md:mr-2"></i>
                  Book Now
                </Button>
              </Link>
              <a href="tel:4438000348">
                <Button size="lg" variant="outline" className="min-w-[160px] md:min-w-[200px] border-white text-white hover:bg-white hover:text-primary text-xs md:text-base py-2 md:py-3">
                  <i className="ri-phone-line mr-1 md:mr-2"></i>
                  (443) 800-0348
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
